/**
 * Allys Page Hero Component
 * 
 * Styled hero section for internal pages with Allys branding
 */

'use client';

import type { PageHeroProps } from '@routeroyal/theme-core';
import { motion } from 'framer-motion';
import Image from 'next/image';

/**
 * Allys Page Hero - Premium styling with grain texture
 */
export function AllysPageHero({
  title,
  subtitle,
  backgroundImage,
  overlayOpacity = 0.6,
  size = 'medium'
}: PageHeroProps) {
  const sizeClasses = {
    small: 'py-20 lg:py-24',
    medium: 'py-28 lg:py-36',
    large: 'py-36 lg:py-52'
  };
  
  return (
    <section className={`relative ${sizeClasses[size]} overflow-hidden`}>
      {/* Background Image */}
      {backgroundImage && (
        <div className="absolute inset-0">
          <Image
            src={backgroundImage}
            alt=""
            fill
            className="object-cover"
            priority
            sizes="100vw"
          />
          <div 
            className="absolute inset-0 bg-linear-to-br from-black/50 via-black/30 to-black/60"
            style={{ opacity: overlayOpacity }}
          />
          
          {/* Grain texture overlay */}
          <div 
            className="absolute inset-0 opacity-[0.15] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '180px 180px'
            }}
          />
        </div>
      )}
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={`text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold mb-6 tracking-tight leading-[0.95] font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${
              backgroundImage ? 'text-white' : 'text-gray-900'
            }`}
            {...(backgroundImage && {
              style: {
                textShadow: '0 4px 60px rgba(0,0,0,0.5), 0 2px 30px rgba(0,0,0,0.4)'
              }
            })}
          >
            {title}
          </motion.h1>
          
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={`text-xl lg:text-2xl leading-relaxed font-light font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${
                backgroundImage ? 'text-white/90' : 'text-gray-600'
              }`}
              {...(backgroundImage && {
                style: {
                  textShadow: '0 2px 30px rgba(0,0,0,0.5)'
                }
              })}
            >
              {subtitle}
            </motion.p>
          )}
        </div>
      </div>
    </section>
  );
}

